// Howie Knowledge Graph — D3 force simulation hook
// ~595 canonical nodes clustered into 6 domain groups with curved relationship links.
// Visual design modeled on Alexandria's galaxy: triple-circle nodes, Bezier curves, DOM tooltips.

import { useEffect, useRef, useCallback } from 'react';
import { select } from 'd3-selection';
import { scaleSqrt } from 'd3-scale';
import { max } from 'd3-array';
import { zoom, zoomIdentity } from 'd3-zoom';
import {
  forceSimulation,
  forceManyBody,
  forceCenter,
  forceCollide,
} from 'd3-force';
import type { ZoomBehavior } from 'd3-zoom';
import 'd3-transition';
import type { Canonical, CanonicalLink, CanonicalGalaxyNode } from '../types';
import {
  DOMAIN_COLORS,
  DOMAIN_LABELS,
  GALAXY_CONFIG,
  RELATIONSHIP_COLORS,
  STANCE_LABELS,
  TEXT_PRIMARY,
} from '../constants';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface UseHowieGalaxySimulationProps {
  canonicals: Canonical[];
  canonicalDomainIndex: Map<string, number>;
  canonicalLinks: CanonicalLink[];
  width: number;
  height: number;
  onNodeClick: (canonicalId: string) => void;
}

// ─── Seeded PRNG (mulberry32) ─────────────────────────────────────────────────

function seededRandom(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function stringHash(s: string): number {
  return s.split('').reduce((h, ch) => (Math.imul(31, h) + ch.charCodeAt(0)) | 0, 0) >>> 0;
}

// ─── Domain cluster force ─────────────────────────────────────────────────────

function domainClusterForce(
  cx: number,
  cy: number,
  clusterRadius: number,
  domainCount: number,
) {
  const clusterCenters = Array.from({ length: domainCount }, (_, i) => {
    const angle = (i / domainCount) * Math.PI * 2 - Math.PI / 2;
    return {
      x: cx + Math.cos(angle) * clusterRadius,
      y: cy + Math.sin(angle) * clusterRadius,
    };
  });

  let nodes: CanonicalGalaxyNode[] = [];

  function force(alpha: number) {
    for (const node of nodes) {
      const center = clusterCenters[node.domainIndex] ?? clusterCenters[0];
      node.vx! += (center.x - node.x!) * alpha * 0.08;
      node.vy! += (center.y - node.y!) * alpha * 0.08;
    }
  }

  force.initialize = (n: CanonicalGalaxyNode[]) => {
    nodes = n;
  };

  return force;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHowieGalaxySimulation({
  canonicals,
  canonicalDomainIndex,
  canonicalLinks,
  width,
  height,
  onNodeClick,
}: UseHowieGalaxySimulationProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  const resetZoom = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current)
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.transform, zoomIdentity);
  }, []);

  const zoomIn = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current)
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.scaleBy, 1.5);
  }, []);

  const zoomOut = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current)
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.scaleBy, 0.67);
  }, []);

  useEffect(() => {
    if (!svgRef.current || canonicals.length === 0 || width === 0 || height === 0) return;

    const svg = select<SVGSVGElement, unknown>(svgRef.current);
    svg.selectAll('*').remove();

    // ── Radius scale ──────────────────────────────────────────────────────────
    const scores = canonicals.map((c) => c.importance_score ?? 0);
    const maxScore = max(scores) ?? 1;
    const radiusScale = scaleSqrt()
      .domain([0, maxScore])
      .range([GALAXY_CONFIG.canonicalMinRadius, GALAXY_CONFIG.canonicalMaxRadius]);

    // ── Build nodes ───────────────────────────────────────────────────────────
    const cx = width / 2;
    const cy = height / 2;
    const clusterRadius = Math.min(width, height) * 0.25;
    const domainCount = Math.max(1, new Set(canonicalDomainIndex.values()).size || 6);

    const nodeMap = new Map<string, CanonicalGalaxyNode>();
    const nodes: CanonicalGalaxyNode[] = canonicals.map((c) => {
      const dIdx = canonicalDomainIndex.get(c.id) ?? 0;
      const score = c.importance_score ?? 0;
      const node: CanonicalGalaxyNode = {
        id: c.id,
        name: c.name,
        domainIndex: dIdx,
        importanceScore: score,
        stance: c.stance,
        radius: radiusScale(score),
        color: DOMAIN_COLORS[dIdx] ?? DOMAIN_COLORS[0],
      };
      nodeMap.set(c.id, node);
      return node;
    });

    // ── Initial positions ─────────────────────────────────────────────────────
    nodes.forEach((node) => {
      const angle = (node.domainIndex / domainCount) * Math.PI * 2 - Math.PI / 2;
      const rng = seededRandom(stringHash(node.id));
      const spread = clusterRadius * 0.5;
      const r = rng() * spread;
      const a = angle + (rng() - 0.5) * Math.PI * 0.7;
      node.x = cx + Math.cos(angle) * clusterRadius + Math.cos(a) * r;
      node.y = cy + Math.sin(angle) * clusterRadius + Math.sin(a) * r;
    });

    // ── Filter links to only those with valid endpoints ───────────────────────
    const validLinks = canonicalLinks.filter(
      (l) => nodeMap.has(l.source) && nodeMap.has(l.target),
    );
    console.log('[HowieGalaxy] Rendering', validLinks.length, 'links out of', canonicalLinks.length);

    // ── Container group + zoom ────────────────────────────────────────────────
    const g = svg.append('g').attr('class', 'howie-galaxy-container');

    // ── Stars background ──────────────────────────────────────────────────────
    const starsG = g.append('g').attr('class', 'stars');
    const starRng = seededRandom(42);
    for (let i = 0; i < 150; i++) {
      starsG
        .append('circle')
        .attr('cx', (starRng() - 0.5) * width * 3 + cx)
        .attr('cy', (starRng() - 0.5) * height * 3 + cy)
        .attr('r', starRng() * 0.8)
        .attr('fill', '#ffffff')
        .attr('opacity', starRng() * 0.2 + 0.03);
    }

    // ── Zoom behavior ─────────────────────────────────────────────────────────
    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 10])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });
    svg.call(zoomBehavior);
    zoomBehaviorRef.current = zoomBehavior;

    // ── Link layer (curved Bezier paths — drawn behind nodes) ─────────────────
    const linkPaths = g
      .append('g')
      .attr('class', 'canonical-links')
      .selectAll<SVGPathElement, CanonicalLink>('path')
      .data(validLinks)
      .join('path')
      .attr('fill', 'none')
      .attr('stroke', (l) => {
        const color = RELATIONSHIP_COLORS[l.type];
        if (color) return color;
        // Same-domain links: use domain color, cross-domain: white
        const sNode = nodeMap.get(l.source);
        const tNode = nodeMap.get(l.target);
        if (sNode && tNode && sNode.domainIndex === tNode.domainIndex) return sNode.color;
        return '#ffffff';
      })
      .attr('stroke-opacity', (l) => {
        // CONTRADICTS and RESONATES_WITH are most visually important
        if (l.type === 'CONTRADICTS') return 0.35;
        if (l.type === 'RESONATES_WITH') return 0.25;
        return 0.15;
      })
      .attr('stroke-width', (l) => {
        if (l.type === 'CONTRADICTS') return 1.5;
        if (l.type === 'RESONATES_WITH') return 1.0;
        return 0.6;
      });

    // ── Node groups (triple-circle design) ────────────────────────────────────
    const nodeGroups = g
      .append('g')
      .attr('class', 'canonical-nodes')
      .selectAll<SVGGElement, CanonicalGalaxyNode>('.canonical-node')
      .data(nodes, (d) => d.id)
      .join('g')
      .attr('class', 'canonical-node')
      .style('cursor', 'pointer');

    // 1. Main circle
    nodeGroups
      .append('circle')
      .attr('class', 'main-circle')
      .attr('r', (d) => d.radius)
      .attr('fill', (d) => d.color)
      .attr('opacity', (d) => 0.55 + d.importanceScore * 0.4);

    // 2. Inner highlight (specular)
    nodeGroups
      .append('circle')
      .attr('r', (d) => d.radius * 0.35)
      .attr('fill', '#ffffff')
      .attr('opacity', 0.1);

    // ── Labels for high-importance nodes ───────────────────────────────────────
    nodeGroups
      .filter((d) => d.importanceScore > 0.75)
      .append('text')
      .text((d) => d.name)
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => d.radius + 12)
      .attr('fill', TEXT_PRIMARY)
      .attr('font-size', '8px')
      .attr('font-family', 'system-ui, sans-serif')
      .attr('opacity', 0.7)
      .style('pointer-events', 'none');

    // ── DOM tooltip (rich, follows mouse) ─────────────────────────────────────
    const tooltipParent = svgRef.current!.parentElement;
    if (!tooltipParent) return;
    const tooltip = select(tooltipParent)
      .append('div')
      .attr('class', 'howie-galaxy-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(10, 10, 15, 0.95)')
      .style('border', '1px solid rgba(255,255,255,0.15)')
      .style('border-radius', '8px')
      .style('padding', '10px 14px')
      .style('color', '#e8e6e3')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('max-width', '260px')
      .style('z-index', '100')
      .style('transition', 'opacity 0.15s');

    // ── Hover + click interactions ────────────────────────────────────────────
    nodeGroups
      .on('mouseenter', function (event, d) {
        // Scale up main circle
        select(this)
          .select<SVGCircleElement>('.main-circle')
          .transition()
          .duration(200)
          .attr('opacity', 1)
          .attr('r', d.radius * 1.15);

        // Build tooltip content with DOM API
        const tooltipNode = tooltip.node()!;
        tooltipNode.textContent = '';

        const nameDiv = document.createElement('div');
        nameDiv.style.fontWeight = '600';
        nameDiv.style.marginBottom = '4px';
        nameDiv.style.color = d.color;
        nameDiv.textContent = d.name;

        const domainDiv = document.createElement('div');
        domainDiv.style.fontSize = '10px';
        domainDiv.style.color = '#888';
        domainDiv.style.marginBottom = '3px';
        domainDiv.textContent = DOMAIN_LABELS[d.domainIndex] ?? 'Unknown';

        tooltipNode.append(nameDiv, domainDiv);

        if (d.stance) {
          const stanceDiv = document.createElement('div');
          stanceDiv.style.fontSize = '10px';
          stanceDiv.style.marginBottom = '2px';
          const label = STANCE_LABELS[d.stance as keyof typeof STANCE_LABELS] ?? d.stance;
          stanceDiv.textContent = label;
          stanceDiv.style.color = d.stance === 'ENDORSED' ? '#4caf50' : d.stance === 'CRITIQUED' ? '#ef5350' : '#90a4ae';
          tooltipNode.append(stanceDiv);
        }

        if (d.importanceScore > 0) {
          const impDiv = document.createElement('div');
          impDiv.style.fontSize = '10px';
          impDiv.style.color = '#666';
          impDiv.textContent = `Importance: ${Math.round(d.importanceScore * 100)}%`;
          tooltipNode.append(impDiv);
        }

        tooltip
          .style('opacity', '1')
          .style('left', `${event.offsetX + 15}px`)
          .style('top', `${event.offsetY - 10}px`);
      })
      .on('mousemove', function (event) {
        tooltip
          .style('left', `${event.offsetX + 15}px`)
          .style('top', `${event.offsetY - 10}px`);
      })
      .on('mouseleave', function (_, d) {
        select(this)
          .select<SVGCircleElement>('.main-circle')
          .transition()
          .duration(200)
          .attr('opacity', 0.85)
          .attr('r', d.radius);

        tooltip.style('opacity', '0');
      })
      .on('click', (_, d) => {
        tooltip.style('opacity', '0');
        onNodeClick(d.id);
      });

    // ── Force simulation ──────────────────────────────────────────────────────
    const simulation = forceSimulation<CanonicalGalaxyNode>(nodes)
      .force('charge', forceManyBody<CanonicalGalaxyNode>().strength(-8))
      .force('center', forceCenter(cx, cy).strength(0.015))
      .force('collision', forceCollide<CanonicalGalaxyNode>().radius((d) => d.radius + 1).iterations(1))
      .force('domainCluster', domainClusterForce(cx, cy, clusterRadius, domainCount))
      .alphaDecay(0.04)
      .on('tick', () => {
        // Update node positions
        nodeGroups.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);

        // Update link paths as curved Bezier arcs
        linkPaths.attr('d', (l) => {
          const s = nodeMap.get(l.source);
          const t = nodeMap.get(l.target);
          if (!s || s.x == null || s.y == null || !t || t.x == null || t.y == null) return '';
          const dx = t.x - s.x;
          const dy = t.y - s.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 1) return '';
          const curvature = dist * 0.15;
          const mx = (s.x + t.x) / 2 - (dy / dist) * curvature;
          const my = (s.y + t.y) / 2 + (dx / dist) * curvature;
          return `M${s.x},${s.y} Q${mx},${my} ${t.x},${t.y}`;
        });
      });

    simulation.alpha(0.6);

    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [canonicals, canonicalDomainIndex, canonicalLinks, width, height, onNodeClick]);

  return { svgRef, resetZoom, zoomIn, zoomOut };
}
